import {
  getDefaultValue,
  Field,
  Validation,
  FieldControllerType,
  ConditionType,
} from '@gemini/lux-ds-pms';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { convertStringToFieldControllerType } from '@utils/functions';
import { useCallback, useMemo } from 'react';

const useMergeForm = () => {
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const currentPatient = useOnlineBookingStore(state => state.currentPatient);

  const validationRequired: Validation = useMemo(() => {
    return {
      type: 'required',
      value: true,
      errorMsgKey: 'fieldsLabel.errors.isRequired',
    };
  }, []);
  const validationTextLength: Validation = useMemo(() => {
    return {
      type: 'maxLength',
      value: 300,
      errorMsgKey: 'fieldsLabel.errors.customMaxLength',
    };
  }, []);

  const addValidation = (field: Field): Field => ({
    ...field,
    validations: field.validations?.some(
      validation => validation.type === validationRequired.type,
    )
      ? field.validations
      : field.validations
        ? [...field.validations, validationRequired]
        : [validationRequired],
  });

  const setHidden = (field: Field): Field => ({
    ...field,
    visibility: false,
  });

  //PATIENT DETAILS FIELDS CHECKS
  const isInterdepPhoneEmailPatientDetails: boolean = useMemo(() => {
    const patientDetailsConfig = formConfigs?.patientDetails;

    return (
      patientDetailsConfig?.email === StaticValues.INTERDEP_PHONENUMBER_EMAIL &&
      patientDetailsConfig?.phone?.number ===
        StaticValues.INTERDEP_PHONENUMBER_EMAIL
    );
  }, [formConfigs]);

  const isPhoneTypeHiddenPatientDetails = useMemo(() => {
    const patientDetailsConfig = formConfigs?.patientDetails;
    if (!patientDetailsConfig) return false;

    return (
      getDefaultValue('patientDetails.phone.type', {
        patientDetails: patientDetailsConfig,
      }) === StaticValues.FIELD_STATUS_HIDDEN
    );
  }, [formConfigs]);

  //CONTACT DETAILS FIELDS CHECKS
  const isInterdepPhoneEmailContactsDetails: boolean = useMemo(() => {
    const contactsDetailsConfig = formConfigs?.contactsDetails;

    return (
      contactsDetailsConfig?.email ===
        StaticValues.INTERDEP_PHONENUMBER_EMAIL &&
      contactsDetailsConfig?.phone?.number ===
        StaticValues.INTERDEP_PHONENUMBER_EMAIL
    );
  }, [formConfigs]);

  const isPhoneTypeHiddenContactsDetails = useMemo(() => {
    const contactsDetailsConfig = formConfigs?.contactsDetails;
    if (!contactsDetailsConfig) return false;

    return (
      getDefaultValue('contactsDetails.phone.type', {
        contactsDetails: contactsDetailsConfig,
      }) === StaticValues.FIELD_STATUS_HIDDEN
    );
  }, [formConfigs]);

  const mergePatientDetails = (
    fields: Field[],
    patientIndex = currentPatient,
  ): Field[] => {
    if (formConfigs) {
      const patientDetailsConfig = formConfigs.patientDetails;

      const mergedForm: Field[] = fields.map(f => {
        const fieldConfig = getDefaultValue(f.id, {
          patientDetails: patientDetailsConfig,
        });
        const field = f;

        if (field.children) {
          return {
            ...field,
            children: mergePatientDetails(field.children, patientIndex),
          };
        }

        // INTERDEP_PHONENUMBER_EMAIL -> mutua interdipendenza
        if (
          (field.id === 'patientDetails.email' ||
            field.id === 'patientDetails.phone.number') &&
          isInterdepPhoneEmailPatientDetails
        ) {
          if (field.id === 'patientDetails.email') {
            // conditions di base
            const conditions: { idRef: string; type: ConditionType }[] = [
              {
                idRef: 'patientDetails.phone.number',
                type: ConditionType.NOT_EMPTY,
              },
            ];

            // aggiungo la condizione sul type SOLO se non è hidden
            if (!isPhoneTypeHiddenPatientDetails) {
              conditions.push({
                idRef: 'patientDetails.phone.type',
                type: ConditionType.NOT_EMPTY,
              });
            }

            const interdepField = {
              ...field,
              interdepCondition: {
                type: 'AND',
                toEvaluateIf: {
                  type: 'required',
                  value: false,
                  errorMsgKey: 'error',
                },
                conditions,
              },
            } as Field;

            return addValidation(interdepField);
          }

          if (field.id === 'patientDetails.phone.number') {
            const interdepField = {
              ...field,
              interdepCondition: {
                type: 'AND',
                toEvaluateIf: {
                  type: 'required',
                  value: false,
                  errorMsgKey: 'error',
                },
                conditions: [
                  {
                    idRef: 'patientDetails.email',
                    type: ConditionType.NOT_EMPTY,
                  },
                  {
                    idRef: 'patientDetails.phone.number',
                    type: ConditionType.EMPTY,
                  },
                ],
              },
            } as Field;
            //interdep condition + add validation in order to make the "empty" check
            return addValidation(interdepField);
          }
        }

        if (fieldConfig === StaticValues.FIELD_STATUS_MANDATORY) {
          return addValidation(field);
        } else if (fieldConfig === StaticValues.FIELD_STATUS_HIDDEN) {
          return setHidden(field);
        } else {
          return field;
        }
      });

      return mergedForm;
    } else {
      return fields;
    }
  };

  const getFormChildren = (formId: FormIds) => {
    switch (formId) {
      case FormIds.CustomFieldsPatient:
        return formConfigs?.patientDetails.customFieldsPatient;
      case FormIds.CustomFieldsInsurance:
        return formConfigs?.patientDetails.customFieldsInsurance;
      case FormIds.CustomPage:
        return formConfigs?.customPage;
      default:
        return null;
    }
  };

  const getCustomFieldsValidations = useCallback(
    (status: StaticValues, type?: string) => {
      // type is absent when we get customFields for patient or insurance (always text)
      if (status === StaticValues.FIELD_STATUS_MANDATORY) {
        if (!type || type.toLowerCase() === 'text')
          return [validationRequired, validationTextLength];
        else return [validationRequired];
      } else {
        if (!type || type.toLowerCase() === 'text')
          return [validationTextLength];
        else return undefined;
      }
    },
    [validationRequired, validationTextLength],
  );

  const createCustomFields = (
    prefix: string,
    formId: FormIds,
    forcingTextArea = false,
  ): Field[] => {
    if (formConfigs) {
      const customFieldsConfig = getFormChildren(formId);
      if (
        customFieldsConfig &&
        customFieldsConfig.show &&
        customFieldsConfig.children?.length > 0
      ) {
        return customFieldsConfig.children.map((c, i) => ({
          id: prefix + '.field' + (i + 1),
          size: {
            xs: 12,
            md: 12,
            lg: 12,
          },
          labelKey:
            formId === FormIds.CustomPage ? `${i + 1}. ${c.label}` : c.label,
          type: forcingTextArea
            ? FieldControllerType.TEXTAREA
            : !c.type || c.type?.toLowerCase() === 'text'
              ? FieldControllerType.TEXTAREA
              : convertStringToFieldControllerType(c.type),
          visibility:
            c.status !== StaticValues.FIELD_STATUS_HIDDEN ? true : false,
          validations: getCustomFieldsValidations(c.status, c.type),
          otherOptions: {
            textArea: {
              maxLength: '300',
              limit: 300,
              minRows: '1',
              resizable: true,
            },
          },
          options:
            c.type && c.type.toLowerCase() === 'radio'
              ? [
                  {
                    key: 'yes',
                    label: 'Yes',
                  },
                  { key: 'no', label: 'No' },
                ]
              : undefined,
        }));
      } else {
        return [];
      }
    } else {
      return [];
    }
  };

  const mergeContactsDetails = (fields: Field[]): Field[] => {
    if (formConfigs) {
      const contactsDetailsConfig = formConfigs.contactsDetails;

      const contactsDetailsFields: Field[] = fields.map(f => {
        const fieldConfig = getDefaultValue(f.id, {
          contactsDetails: contactsDetailsConfig,
        });

        if (f.children) {
          return {
            ...f,
            children: mergeContactsDetails(f.children),
          };
        }
        // INTERDEP_PHONENUMBER_EMAIL -> mutua interdipendenza (contactsDetails)
        if (
          (f.id === 'contactsDetails.email' ||
            f.id === 'contactsDetails.phone.number') &&
          isInterdepPhoneEmailContactsDetails
        ) {
          if (f.id === 'contactsDetails.email') {
            const conditions: { idRef: string; type: ConditionType }[] = [
              {
                idRef: 'contactsDetails.phone.number',
                type: ConditionType.NOT_EMPTY,
              },
            ];

            if (!isPhoneTypeHiddenContactsDetails) {
              conditions.push({
                idRef: 'contactsDetails.phone.type',
                type: ConditionType.NOT_EMPTY,
              });
            }

            const interdepField = {
              ...f,
              interdepCondition: {
                type: 'AND',
                toEvaluateIf: {
                  type: 'required',
                  value: false,
                  errorMsgKey: 'error',
                },
                conditions,
              },
            } as Field;

            return addValidation(interdepField);
          }

          if (f.id === 'contactsDetails.phone.number') {
            const interdepField = {
              ...f,
              interdepCondition: {
                type: 'AND',
                toEvaluateIf: {
                  type: 'required',
                  value: false,
                  errorMsgKey: 'error',
                },
                conditions: [
                  {
                    idRef: 'contactsDetails.email',
                    type: ConditionType.NOT_EMPTY,
                  },
                  {
                    idRef: 'contactsDetails.phone.number',
                    type: ConditionType.EMPTY,
                  },
                ],
              },
            } as Field;

            return addValidation(interdepField);
          }
        }

        if (fieldConfig === StaticValues.FIELD_STATUS_MANDATORY) {
          return addValidation(f);
        } else if (fieldConfig === StaticValues.FIELD_STATUS_HIDDEN) {
          return setHidden(f);
        } else {
          return f;
        }
      });
      return contactsDetailsFields;
    } else return fields;
  };

  return {
    mergePatientDetails,
    createCustomFields,
    mergeContactsDetails,
  };
};

export default useMergeForm;
